class VorugLoader extends HTMLElement {
 connectedCallback(){
  this.events?.abort();
  this.pending ??= new Set();
  this.generation ??= 0;
  this.media=matchMedia('(prefers-reduced-motion: reduce)');
  this.events=new AbortController();
  const options={signal:this.events.signal};
  this.onVisibility=()=>{if(this.hasAttribute('data-visible')&&!this.media.matches)this.loadArtwork();
   if(!document.hidden&&this.pending.size&&!this.dismissed&&!this.hasAttribute('data-visible')&&!this.timer)this.show();
   this.motion();
  };
  this.addEventListener('keydown',e=>{
   if(e.key==='Escape'){e.preventDefault();this.dismiss()}
   if(e.key==='Tab'&&this.hasAttribute('data-visible')){e.preventDefault();this.focus({preventScroll:true})}
  },options);
  document.addEventListener('visibilitychange',this.onVisibility,options);
  this.media.addEventListener('change',this.onVisibility,options);
  this.querySelector('object')?.addEventListener('load',()=>this.motion(),options);
  window.addEventListener('pageshow',event=>{if(event.persisted)this.reset()},options);
  window.addEventListener('pagehide',()=>this.reset(),options);
  this.addEventListener('transitionend',e=>{if(e.target===this&&e.propertyName==='opacity')this.motion()},options);
  this.motion();
 }
 motion(){
  try{
   const svg=this.artRoot?.querySelector('svg')||this.querySelector('object')?.contentDocument?.documentElement;
   if(!svg?.pauseAnimations)return;
   if(this.media.matches){svg.pauseAnimations();svg.setCurrentTime(1.8)}
   else if(document.hidden||(!this.hasAttribute('data-visible')&&!this.exiting))svg.pauseAnimations();
   else svg.unpauseAnimations();
  }catch{/* Asset failure must never prevent request completion or cleanup. */}
 }
 async loadArtwork(){
  if(this.artRoot?.querySelector('svg')||this.artLoading)return;
  const host=this.querySelector('.vorug-loader-art');if(!host)return;
  const fallback=host.querySelector('img');if(fallback&&!fallback.hasAttribute('src'))fallback.setAttribute('src',fallback.dataset.src);
  if(this.media.matches)return;
  this.artLoading=true;this.artRequest=new AbortController();
  const generation=this.generation;
  try{
   const response=await fetch(host.dataset.src,{signal:this.artRequest.signal,credentials:'omit'});
   if(!response.ok)throw Error('Artwork unavailable');
   const text=await response.text();
   if(!this.isConnected||generation!==this.generation)return;
   const svg=new DOMParser().parseFromString(text,'image/svg+xml').documentElement;
   if(svg.localName!=='svg')throw Error('Invalid artwork');
   this.artRoot=host.shadowRoot||host.attachShadow({mode:'open'});
   const style=document.createElement('style');style.textContent=':host{display:block;position:relative;width:160px;height:144px}img{position:absolute;inset:0;width:160px;height:144px;animation:still-out .24s ease-out forwards}svg{display:block;width:160px;height:auto;max-height:144px;animation:art-in .24s ease-out both}@keyframes still-out{to{opacity:0}}@keyframes art-in{from{opacity:0}to{opacity:1}}@media(prefers-reduced-motion:reduce){img{display:none}svg{animation:none}}';
   this.artRoot.replaceChildren(style,...(fallback?[fallback.cloneNode(true)]:[]),svg);this.motion();this.startIntro();
  }catch{}finally{this.artLoading=false}
 }
 introduce(){
  if(!this.isConnected||document.hidden)return false;
  clearTimeout(this.introTimer);clearTimeout(this.introDeadline);clearTimeout(this.exitTimer);this.introTimer=null;
  this.exiting=false;this.intro=true;
  this.setAttribute('data-intro','');this.present();
  const still=this.querySelector('.vorug-loader-art img');
  still?.addEventListener('load',()=>this.startIntro(),{once:true,signal:this.events.signal});
  this.introDeadline=setTimeout(()=>this.hide(),2500);
  this.loadArtwork();this.motion();this.startIntro();
  return true;
 }
 startIntro(){
  if(!this.intro||this.introTimer)return;
  const still=this.querySelector('.vorug-loader-art img');
  if(!still?.naturalWidth&&!this.artRoot?.querySelector('svg'))return;
  clearTimeout(this.introDeadline);
  this.introTimer=setTimeout(()=>this.hide(),1800);
  this.dispatchEvent(new Event('vorug:intro-shown'));
 }
 present(){
  if(!this.contains(document.activeElement))this.previousFocus=document.activeElement;
  this.setAttribute('data-visible','');this.removeAttribute('aria-hidden');this.inert=false;
  this.blocked??=[];
  this.blocked.push(...[...document.body.children].filter(el=>el!==this&&!el.contains(this)&&!el.inert&&!['SCRIPT','STYLE','LINK'].includes(el.tagName)));
  this.blocked.forEach(el=>el.inert=true);
  if(!this.savedOverflow){
   this.savedOverflow={value:document.body.style.getPropertyValue('overflow'),priority:document.body.style.getPropertyPriority('overflow')};
   document.body.style.setProperty('overflow','hidden');
  }
  this.focus({preventScroll:true});this.motion();
 }
 show(){
  if(!this.isConnected||document.hidden||this.dismissed||!this.pending.size)return;
  clearTimeout(this.exitTimer);this.exiting=false;
  this.removeAttribute('data-intro');this.present();this.loadArtwork();
  clearTimeout(this.escape);this.escape=setTimeout(()=>this.dismiss(),4000);
 }
 begin(){
  if(window.Shopify?.designMode)return ()=>{};
  if(this.intro){this.hide();clearTimeout(this.exitTimer);this.exiting=false;}
  const generation=this.generation??0;
  const token=Symbol();this.pending.add(token);
  if(!this.dismissed&&!this.timer&&!this.hasAttribute('data-visible')){
   this.timer=setTimeout(()=>{this.timer=null;this.show()},180);
  }
  let finished=false;
  const finish=()=>{
   if(finished)return;
   finished=true;clearTimeout(deadline);this.expiries.delete(deadline);
   if(generation!==this.generation)return;
   this.pending.delete(token);
   if(!this.pending.size){this.dismissed=false;this.hide()}
  };
  this.expiries??=new Set();
  const deadline=setTimeout(finish,15000);this.expiries.add(deadline);
  return finish;
 }
 async track(work){
  const finish=this.begin();
  try{return await work()}finally{finish()}
 }
 reset(){
  this.generation=(this.generation??0)+1;
  this.expiries?.forEach(clearTimeout);this.expiries?.clear();
  this.pending.clear();this.dismissed=false;this.hide();
 }
 dismiss(){this.dismissed=!!this.pending?.size;this.hide()}
 hide(){
  const wasIntro=this.intro;clearTimeout(this.introTimer);clearTimeout(this.introDeadline);this.intro=false;
  clearTimeout(this.timer);clearTimeout(this.escape);clearTimeout(this.exitTimer);this.timer=null;
  const visible=this.hasAttribute('data-visible');
  this.removeAttribute('data-visible');this.setAttribute('aria-hidden','true');this.inert=true;
  this.blocked?.forEach(el=>el.inert=false);this.blocked=[];
  if(this.savedOverflow){
   const {value,priority}=this.savedOverflow;
   if(value)document.body.style.setProperty('overflow',value,priority);
   else document.body.style.removeProperty('overflow');
   this.savedOverflow=null;
  }
  const focused=this.contains(document.activeElement);
  if(focused&&this.previousFocus?.isConnected)this.previousFocus.focus({preventScroll:true});
  this.exiting=visible&&!this.media?.matches&&!wasIntro;
  this.exitTimer=setTimeout(()=>{this.exiting=false;this.motion()},this.exiting?340:0);
 }
 disconnectedCallback(){
  this.reset();clearTimeout(this.exitTimer);this.exiting=false;this.motion();this.events?.abort();
  this.artRequest?.abort();
 }
}
if(!customElements.get('vorug-loader'))customElements.define('vorug-loader',VorugLoader);

(() => {
 const loader=document.querySelector('vorug-loader');if(!loader)return;
 window.VorugLoading={begin:()=>loader.begin(),track:work=>loader.track(work)};
 if(window.Shopify?.designMode)return;

 // Only actual same-tab, same-origin document navigations; local controls keep their own feedback.
 const navigation=()=>{
  const finish=loader.begin();
  setTimeout(finish,4000);
 };
 document.addEventListener('click',event=>{
  if(event.defaultPrevented||event.button!==0||event.metaKey||event.ctrlKey||event.shiftKey||event.altKey)return;
  const link=event.target.closest?.('a[href]');
  if(!link||link.hasAttribute('download')||link.getAttribute('aria-disabled')==='true'||(link.target&&link.target!=='_self'))return;
  const target=new URL(link.href,location.href);
  if(target.origin!==location.origin||!/^https?:$/.test(target.protocol))return;
  if(target.pathname===location.pathname&&target.search===location.search)return;
  navigation();
 });
 document.addEventListener('submit',event=>{
  if(event.defaultPrevented||!event.target.checkValidity())return;
  const form=event.target;
  if(form.target&&form.target!=='_self')return;
  const target=new URL(form.action||location.href,location.href);
  if(target.origin===location.origin&&/^https?:$/.test(target.protocol))navigation();
 });

 if(loader.dataset.initial!=='true')return;
 const key='vorug-fullscreen-seen';
 let seen=false;try{seen=sessionStorage.getItem(key)==='1'}catch{}
 const start=()=>{
  if(!seen){
   loader.addEventListener('vorug:intro-shown',()=>{try{sessionStorage.setItem(key,'1')}catch{}},{once:true});
   loader.introduce();
   return;
  }
  // Returning visits use genuine unfinished visible images, without a forced presentation delay.
  const images=[...document.images].filter(img=>{
   const rect=img.getBoundingClientRect();
   return img.loading!=='lazy'&&!img.complete&&rect.width>0&&rect.height>0&&rect.bottom>0&&rect.top<innerHeight;
  });
  if(!images.length)return;
  const events=new AbortController();let deadline;
  loader.track(()=>Promise.race([
   Promise.all(images.map(img=>new Promise(resolve=>{
    img.addEventListener('load',resolve,{once:true,signal:events.signal});
    img.addEventListener('error',resolve,{once:true,signal:events.signal});
    if(img.complete)resolve();
   }))),
   new Promise(resolve=>{deadline=setTimeout(resolve,1500)})
  ])).finally(()=>{clearTimeout(deadline);events.abort()});
 };
 if(document.hidden){
  const visible=()=>{if(!document.hidden){document.removeEventListener('visibilitychange',visible);start()}};
  document.addEventListener('visibilitychange',visible);
 }else start();
})();
